// React Query hooks for events. Thin wrappers over lib/api/events.ts.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listEvents,
  getEvent,
  createEvent,
  closeEvent,
  updateEvent,
  getEventSummary,
  isEventCodeAvailable,
  generateEventCode,
  reopenEvent,
  archiveEvent,
  unarchiveEvent,
  voidEvent,
  type CreateEventInput,
  type UpdateEventInput,
} from '@/lib/api/events';
import { useActiveEventStore } from '@/hooks/useActiveEvent';
import type { EventStatus } from '@/types/domain';

const KEYS = {
  all: ['events'] as const,
  list: (filters?: object) => [...KEYS.all, 'list', filters ?? {}] as const,
  detail: (id: string) => [...KEYS.all, 'detail', id] as const,
  summary: (id: string) => [...KEYS.all, 'summary', id] as const,
  codeAvailable: (code: string, excludeId?: string) =>
    [...KEYS.all, 'code-available', code, excludeId ?? ''] as const,
};

// ==== Queries ====

export const useEvents = (filters?: { status?: EventStatus; includeHidden?: boolean }) =>
  useQuery({
    queryKey: KEYS.list(filters),
    queryFn: () => listEvents(filters),
  });

/** Active events list — current open events. */
export const useActiveEvents = () =>
  useQuery({
    queryKey: KEYS.list({ status: 'active' }),
    queryFn: () => listEvents({ status: 'active' }),
  });

/** Archived events — hidden from the working list until someone asks. */
export const useArchivedEvents = () =>
  useQuery({
    queryKey: KEYS.list({ status: 'archived' }),
    queryFn: () => listEvents({ status: 'archived' }),
  });

/** Closed events history. */
export const useEventHistory = () =>
  useQuery({
    queryKey: KEYS.list({ status: 'closed' }),
    queryFn: () => listEvents({ status: 'closed' }),
  });

export const useEvent = (id: string | null) =>
  useQuery({
    queryKey: KEYS.detail(id ?? ''),
    queryFn: () => (id ? getEvent(id) : Promise.resolve(null)),
    enabled: !!id,
  });

/** Summary (totals + top products) — used by close-event dialog. */
export const useEventSummary = (id: string | null) =>
  useQuery({
    queryKey: KEYS.summary(id ?? ''),
    queryFn: () => (id ? getEventSummary(id) : Promise.resolve(null)),
    enabled: !!id,
  });

/** Check if an event code is available (debounced by caller). */
export const useIsEventCodeAvailable = (code: string, excludeId?: string) =>
  useQuery({
    queryKey: KEYS.codeAvailable(code, excludeId),
    queryFn: () => isEventCodeAvailable(code, excludeId),
    enabled: code.trim().length > 0,
    staleTime: 10 * 1000,
  });

// ==== Mutations ====

export const useCreateEvent = () => {
  const qc = useQueryClient();
  const setActiveEvent = useActiveEventStore((s) => s.setActiveEvent);
  return useMutation({
    mutationFn: (input: CreateEventInput) => createEvent(input),
    onSuccess: (newId) => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      // Auto-select the newly created event so the cashier lands somewhere
      // useful. Name is unknown at this layer — keep null, TopBar will refresh.
      setActiveEvent(newId);
    },
  });
};

export const useUpdateEvent = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateEventInput }) =>
      updateEvent(id, input),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.id) });
    },
  });
};

/**
 * Close an event, recording what physically came back to the warehouse.
 *
 * The returns map belongs to this call, not to a separate one before it:
 * close_event() writes the counts *and* flips the status in one transaction,
 * and it refuses a second call with 'Event sudah ditutup'. Recording returns
 * and then closing was therefore always one RPC too many — the first call had
 * already closed the event, so the second reliably failed on every event that
 * had allocations.
 */
export const useCloseEvent = () => {
  const qc = useQueryClient();
  const clearActive = useActiveEventStore((s) => s.clear);
  return useMutation({
    mutationFn: ({ id, returns }: { id: string; returns?: Record<string, number> }) =>
      closeEvent(id, returns ?? {}),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      qc.invalidateQueries({ queryKey: KEYS.detail(id) });
      qc.invalidateQueries({ queryKey: KEYS.summary(id) });
      // close_event() also writes qty_returned_to_gudang and the audit row, so
      // the allocation views for this event are stale too.
      qc.invalidateQueries({ queryKey: ['event-allocations'] });
      // If the user had the closed event selected as their active event,
      // clear it so the TopBar doesn't keep a stale reference.
      const active = useActiveEventStore.getState().activeEventId;
      if (active === id) clearActive();
    },
  });
};

// ==== Helpers ====

/** Re-export of the code generator so forms can pre-fill an editable default. */
export const useSuggestedEventCode = () => generateEventCode;

// ==== Lifecycle ====
//
// All four invalidate the whole event tree rather than patching one entry:
// these change which list an event belongs to, and the active-event selector,
// the POS picker and the history view all read from that tree.

export const useReopenEvent = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => reopenEvent(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
};

export const useArchiveEvent = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => archiveEvent(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
};

export const useUnarchiveEvent = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unarchiveEvent(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
};

export const useVoidEvent = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => voidEvent(id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
};
